'use client'

import { getMediaConfig } from '@/config/media-upload'
import { IMediaItem, IMediaPreview, IUploadProgress, MediaError } from '@/lib/types/media/core'
import { createStoreMetadata } from '@/lib/types/media/metadata'
import { generateUUID } from '@/lib/utils/crypto'
import { showToast } from '@/lib/utils/toast'
import { formatFileSize } from '@/utils/format-file-size'
import { Modal } from '@heroui/react'
import { MediaPlacement, MediaType } from '@prisma/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { MediaDropzone } from '../core/MediaDropzone'
import { UploadPreview } from '../upload/UploadPreview'

interface MediaModalProps {
  isOpen: boolean
  onClose: () => void
  placement: MediaPlacement
  onUploadComplete?: (media: IMediaItem) => void
  onUploadError?: (error: Error) => void
  ownerId: string
  title?: string
}

export function MediaModal({
  isOpen,
  onClose,
  placement,
  onUploadComplete,
  onUploadError,
  ownerId,
  title = 'Upload Media'
}: MediaModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<IMediaPreview | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const config = getMediaConfig(placement)

  const handleFileSelect = useCallback(
    async (files: File[]) => {
      const file = files[0]
      if (file) {
        try {
          // Create preview using URL.createObjectURL
          const previewUrl = URL.createObjectURL(file)
          const preview: IMediaPreview = {
            previewId: generateUUID(),
            previewUrl,
            file,
            uploadProgress: 0,
            processingStatus: 'pending',
            type: file.type.startsWith('image') ? MediaType.IMAGE : MediaType.VIDEO,
            placement,
            mimeType: file.type,
            size: file.size,
            metadata: {
              placement,
              ownerId,
              alt: null,
              caption: null,
              orderIndex: null
            }
          }
          setFile(file)
          setPreview(preview)
        } catch (error) {
          console.error('Preview creation error:', error)
          const errorMessage = error instanceof MediaError ? error.message : 'Failed to create preview'
          showToast({ title: errorMessage, color: 'danger', timeout: 5000 })
          onUploadError?.(error instanceof Error ? error : new Error(errorMessage))
        }
      }
    },
    [ownerId, onUploadError, placement]
  )

  const handleDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      const errors = rejectedFiles.map((rejection) => {
        if (rejection.errors[0].code === 'file-too-large') {
          return `File size (${formatFileSize(rejection.file.size)}) exceeds limit of ${formatFileSize(config.maxSize)}`
        }
        if (rejection.errors[0].code === 'file-invalid-type') {
          return `File type ${rejection.file.type || 'unknown'} is not supported. Accepted types: ${Object.keys(config.acceptedTypes).join(', ')}`
        }
        return rejection.errors[0].message
      })
      showToast({ title: 'File validation failed: ' + errors.join(', '), color: 'danger', timeout: 5000 })
    },
    [config]
  )

  const handleUpload = async () => {
    if (!file || !preview) return

    try {
      setIsUploading(true)
      abortControllerRef.current = new AbortController()

      // Upload using fetch to /api/upload
      const formData = new FormData()
      formData.append('file', file)
      formData.append('placement', placement)
      formData.append('userId', ownerId)
      formData.append('uploadId', `upload-${Date.now()}`)

      const uploadResult = await new Promise<{ success: boolean; media?: IMediaItem; error?: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            setPreview((prev) =>
              prev
                ? {
                    ...prev,
                    uploadProgress: progress,
                    processingStatus: progress < 100 ? 'uploading' : 'processing'
                  }
                : null
            )
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText)
              if (response.success && response.media) {
                resolve({ success: true, media: response.media })
              } else {
                resolve({ success: false, error: response.error || 'Upload failed' })
              }
            } catch (error) {
              resolve({ success: false, error: 'Failed to parse response' })
            }
          } else {
            resolve({ success: false, error: `Upload failed with status ${xhr.status}` })
          }
        })

        xhr.addEventListener('error', () => {
          resolve({ success: false, error: 'Upload failed' })
        })

        xhr.open('POST', '/api/upload')
        xhr.withCredentials = true
        xhr.send(formData)
      })

      const result = uploadResult

      if (!result.success || !result.media) {
        throw result.error || new MediaError('UPLOAD_ERROR', 'Upload failed')
      }

      // Call onUploadComplete with the validated MediaItem
      onUploadComplete?.(result.media)
      handleClose()
    } catch (error) {
      // Handle abort separately
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }

      // Update preview state with error
      setPreview((prev) =>
        prev
          ? {
              ...prev,
              error:
                error instanceof MediaError
                  ? error
                  : new MediaError('UPLOAD_ERROR', error instanceof Error ? error.message : 'Upload failed')
            }
          : null
      )

      console.error('Upload error:', error)
      const errorMessage = error instanceof MediaError ? error.message : 'Upload failed'
      showToast({ title: errorMessage, color: 'danger', timeout: 5000 })
      onUploadError?.(error instanceof Error ? error : new Error(errorMessage))
    } finally {
      setIsUploading(false)
      abortControllerRef.current = null
    }
  }

  const handleClose = useCallback(() => {
    // Cleanup preview
    if (preview?.previewUrl) {
      try {
        URL.revokeObjectURL(preview.previewUrl)
      } catch (error) {
        console.warn('Failed to revoke preview URL:', preview.previewUrl, error)
      }
    }
    setFile(null)
    setPreview(null)
    setIsUploading(false)
    onClose()
  }, [preview, onClose])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preview?.previewUrl) {
        try {
          URL.revokeObjectURL(preview.previewUrl)
        } catch (error) {
          console.warn('Failed to revoke preview URL:', preview.previewUrl, error)
        }
      }
    }
  }, [preview])

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <Modal.Container>
        <Modal.Dialog>
          <Modal.Header>{title}</Modal.Header>
          <Modal.Body>
      <div className='space-y-4'>
        {!preview ? (
          <div className='p-4 bg-gray-50 rounded-lg'>
            <MediaDropzone
              onDrop={handleFileSelect}
              onDropRejected={handleDropRejected}
              acceptedTypes={config.acceptedTypes}
              maxSize={config.maxSize}
              multiple={false}
              className='bg-white'
            />
          </div>
        ) : (
          <div className='space-y-4'>
            <UploadPreview
              preview={preview}
              onCancel={() => setPreview(null)}
              aspectRatio='video'
              isUploading={isUploading}
              progress={preview?.uploadProgress || 0}
            />
            {!isUploading && (
              <div className='flex justify-end gap-2'>
                <button
                  onClick={() => setPreview(null)}
                  className='px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md'
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  className='px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md'
                >
                  Upload
                </button>
              </div>
            )}
          </div>
        )}
      </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
